import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { formatUZS } from '@/lib/format';
import { salesByCategory, type SaleLike } from '@/lib/report-metrics';
import { CHART_COLORS, ChartTooltipBox } from './chart-bits';
import { ExportButtons } from './ExportButtons';

export function CategoryBreakdown({ sales }: { sales: SaleLike[] }) {
  const { t } = useTranslation();
  const rows = salesByCategory(sales);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="p-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-medium text-foreground">{t('tick_reports.catTitle')}</h3>
        <ExportButtons
          fileName="kategoriyalar"
          title={t('tick_reports.catExportTitle')}
          columns={[
            { header: t('tick_reports.colCategory'), key: 'category' },
            { header: t('tick_reports.colQty'), key: 'quantity' },
            { header: t('tick_reports.colSum'), key: 'sum' },
            { header: t('tick_reports.colShare'), key: 'pct' },
          ]}
          rows={rows.map((r) => ({
            category: r.category,
            quantity: r.quantity,
            sum: formatUZS(r.sum),
            pct: `${r.pct.toFixed(1)}%`,
          }))}
        />
      </div>

      {rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">{t('tick_reports.noData')}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey="sum" nameKey="category" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {rows.map((r, i) => (
                    <Cell key={r.category} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) =>
                    active && payload?.length ? (
                      <ChartTooltipBox
                        title={String(payload[0]?.payload?.category ?? '')}
                        rows={[
                          { label: t('tick_reports.colSum'), value: formatUZS(Number(payload[0]?.payload?.sum ?? 0)) },
                          { label: t('tick_reports.colShare'), value: `${Number(payload[0]?.payload?.pct ?? 0).toFixed(1)}%` },
                        ]}
                      />
                    ) : null
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="divide-y divide-border">
            {rows.map((r, i) => (
              <div key={r.category} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                  />
                  <span className="text-sm text-foreground">{r.category}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">{formatUZS(r.sum)}</p>
                  <p className="text-xs text-muted-foreground">{r.pct.toFixed(1)}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
